import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { C } from '../theme';

const pageSx = {
  pt: '110px',
  pb: 8,
  minHeight: '100vh',
  backgroundColor: C.bg,
  backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
};
const paperSx = { p: { xs: 3, md: 5 }, backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}` };

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [error, setError] = useState('');

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setStatus('error');
      setError('Token za resetovanje lozinke nije pronađen');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Lozinke se ne poklapaju');
      return;
    }

    if (newPassword.length < 6) {
      setError('Lozinka mora imati najmanje 6 karaktera');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/reset-password', { token, newPassword });
      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      setError(error.response?.data?.message || 'Greška pri resetovanju lozinke');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'error' && !token) {
    return (
      <Box sx={pageSx}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
              Resetovanje lozinke
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ ...paperSx, textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 80, color: C.danger, mb: 3 }} />
            <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
              Nevažeći link
            </Typography>
            <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/forgot-password')} sx={{ px: 4, py: 1.5 }}>
              Zatražite novi link
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Postavite novu lozinku
          </Typography>
          <Typography variant="h6" sx={{ color: C.muted, maxWidth: 500, mx: 'auto', lineHeight: 1.6, fontWeight: 400, fontSize: '1.05rem' }}>
            Unesite vašu novu lozinku
          </Typography>
        </Box>

        <Paper elevation={0} sx={paperSx}>
          {status === 'form' && (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                  {error}
                </Alert>
              )}

              <TextField
                required
                fullWidth
                label="Nova lozinka"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Unesite novu lozinku"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <TextField
                required
                fullWidth
                label="Potvrdite novu lozinku"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ponovo unesite novu lozinku"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 4 }}
              />

              <Button type="submit" fullWidth variant="contained" disabled={loading || !newPassword || !confirmPassword} sx={{ py: 1.5, fontSize: '1.05rem' }}>
                {loading ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Postavite novu lozinku'}
              </Button>
            </Box>
          )}

          {status === 'success' && (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: C.success, mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                Lozinka uspešno promenjena!
              </Typography>
              <Typography variant="body1" sx={{ color: C.muted, mb: 4, lineHeight: 1.6 }}>
                Vaša lozinka je uspešno resetovana. Sada se možete prijaviti sa novom lozinkom.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
                Prijavite se
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box sx={{ textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 80, color: C.danger, mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                Greška pri resetovanju
              </Typography>
              <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
                {error}
              </Alert>
              <Button variant="contained" onClick={() => navigate('/forgot-password')} sx={{ px: 4, py: 1.5 }}>
                Zatražite novi link
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
